import { Button, Text } from '@expressable/syntax-ds';
import { CalendarBlank, Clock } from '@phosphor-icons/react';
import { useContentfulTherapist } from 'components/ContentfulTherapistCard/useContentfulTherapist';
import { PlaceholderImg } from 'components/LoadingImage/LoadingImage';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { isFunction } from 'lodash';
import { useTranslation } from 'react-i18next';

type Props = {
  therapistName: string;
  reservationTitle: string;
  reservationSubtitle: string;
  firstSession: Date;
  secondSession?: Date;

  onEditFirstSession?: () => void;
  onEditSecondSession?: () => void;
};

export const TherapistReservationCard = ({
  therapistName,
  reservationTitle,
  reservationSubtitle,
  firstSession,
  secondSession,
  onEditFirstSession,
  onEditSecondSession,
}: Props) => {
  const { data: therapistData } = useContentfulTherapist(therapistName);
  const { t } = useTranslation();

  return (
    <div className="border border-warm-gray-200 divide-y divide-warm-gray-200 bg-white w-full p-0 rounded-lg">
      <div className="flex gap-e12 p-e32">
        {therapistData && (
          <PlaceholderImg
            className="rounded-full size-e80 mobile-d:size-e64"
            src={therapistData.fields.picture.fields.file?.url as string}
          />
        )}
        <div className="flex flex-col gap-e4">
          <Text variant="overline" className="text-warm-gray-600 normal-case">
            {reservationSubtitle}
          </Text>
          <Text variant="h5">{reservationTitle}</Text>

          <Text variant="body-md" className="text-gray-600">
            {t('therapistReservationCard.reservationWith', { therapist: therapistName })}
          </Text>
        </div>
      </div>

      <div className="grid grid-cols-2 mobile-d:grid-cols-1 p-e32 gap-e32">
        <Reservation
          onEdit={onEditFirstSession}
          time={firstSession}
          title={t('therapistReservationCard.firstSession.title')}
        />

        {secondSession && (
          <Reservation
            onEdit={onEditSecondSession}
            time={secondSession}
            title={t('therapistReservationCard.secondSession.title')}
          />
        )}
      </div>
    </div>
  );
};

type ReservationProps = {
  time: Date;
  title: string;
  onEdit?: () => void;
};

const Reservation = ({ onEdit, time, title }: ReservationProps) => {
  const { t } = useTranslation();
  const { format } = useDisplayTimezone();
  return (
    <div>
      <div className="mb-e16 flex gap-e8 items-center">
        <Text variant="h6">{title}</Text>
        {isFunction(onEdit) && (
          <Button onClick={onEdit} variant="link" size="xs">
            {t('therapistReservationCard.edit')}
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-e8  text-warm-gray-600">
        <div className="flex gap-e12 items-center">
          <CalendarBlank className="size-e20 shrink-0" weight="bold" />{' '}
          <Text variant="body-md">{format(time, "EEEE's starting on' MM/dd/yyyy")}</Text>
        </div>
        <div className="flex gap-e12 items-center">
          <Clock className="size-e20 shrink-0" weight="bold" />{' '}
          <Text variant="body-md">{format(time, 'h:mm a (z)')}</Text>
        </div>
      </div>
    </div>
  );
};
